import { useCallback, useEffect, useState } from 'react';

/**
 * HUJJATDAGI TANLOV — satr `id` lari, jadval qatorlaridan MUSTAQIL.
 *
 * Tanlov komponent holatida turganda uni har qanday qayta o'rnatish
 * (sahifa qobig'ining qayta chizilishi, xato chegarasi, token
 * yangilanishi) tozalab yuborardi va skladchi 20 ta terib chiqqan
 * satrini qaytadan belgilardi. Endi u hujjat bo'yicha `sessionStorage`
 * da saqlanadi: shu tabda sahifa qayta o'rnatilsa ham tanlov qaytadi.
 *
 * `sessionStorage` — ataylab `localStorage` emas: tanlov bir ish
 * seansiga tegishli; boshqa tab yoki ertangi kun eski tanlovni meros
 * qilib olmasligi kerak.
 */

const keyOf = (reportId: string): string => `eombor:supply-selection:${reportId}`;

const read = (reportId: string): ReadonlySet<string> => {
  try {
    const raw = sessionStorage.getItem(keyOf(reportId));
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((id): id is string => typeof id === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
};

const write = (reportId: string, ids: ReadonlySet<string>): void => {
  try {
    if (ids.size === 0) sessionStorage.removeItem(keyOf(reportId));
    else sessionStorage.setItem(keyOf(reportId), JSON.stringify([...ids]));
  } catch {
    // Saqlab bo'lmasa (yopiq rejim) tanlov baribir xotirada ishlaydi.
  }
};

export type SelectionUpdate =
  | ReadonlySet<string>
  | ((current: ReadonlySet<string>) => ReadonlySet<string>);

export const useSupplySelection = (
  reportId: string,
): [ReadonlySet<string>, (next: SelectionUpdate) => void] => {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => read(reportId));

  // Boshqa hujjatga o'tilganda — o'sha hujjatning tanlovi.
  useEffect(() => {
    setSelected(read(reportId));
  }, [reportId]);

  const update = useCallback(
    (next: SelectionUpdate): void => {
      setSelected((current) => {
        const value = typeof next === 'function' ? next(current) : next;
        write(reportId, value);
        return value;
      });
    },
    [reportId],
  );

  return [selected, update];
};

/**
 * Yangi ma'lumot kelganda tanlovni YARASHTIRADI: hali ro'yxatda turgan va
 * ishi qolgan satrlar qoladi, yo'qolgan yoki boshqa skladchi joylashtirib
 * bo'lganlari chiqariladi. O'zgarish bo'lmasa AYNI to'plam qaytadi —
 * keraksiz qayta chizish bo'lmasin.
 */
export const reconcileSelection = (
  selected: ReadonlySet<string>,
  stillSelectable: ReadonlySet<string>,
): ReadonlySet<string> => {
  let changed = false;
  const next = new Set<string>();
  for (const id of selected) {
    if (stillSelectable.has(id)) next.add(id);
    else changed = true;
  }
  return changed ? next : selected;
};
